import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Printer, Loader2, Check } from "lucide-react";
import { invoicesApi, accountSummariesApi } from "../../../services/api";
import { BuilderProvider, useBuilder, BlockConfig, generateBlockId } from "./BuilderContext";
import BlockDrawer from "./BlockDrawer";
import DocumentPreview from "./DocumentPreview";
import IssueAndSendModal from "./IssueAndSendModal";

// Default block configs
const INVOICE_DEFAULTS: BlockConfig[] = [
  { id: generateBlockId(), blockType: "header", enabled: true, locked: false, order: 0, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "recipient", enabled: true, locked: false, order: 1, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "matter_details", enabled: true, locked: false, order: 2, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "fees_table", enabled: true, locked: false, order: 3, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "disbursements_table", enabled: true, locked: false, order: 4, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "time_entries_table", enabled: false, locked: false, order: 5, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "totals_summary", enabled: true, locked: true, order: 6, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "payment_history", enabled: false, locked: false, order: 7, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "notes", enabled: false, locked: false, order: 8, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "footer", enabled: false, locked: false, order: 9, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "signature", enabled: false, locked: false, order: 10, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
];

const SUMMARY_DEFAULTS: BlockConfig[] = [
  { id: generateBlockId(), blockType: "header", enabled: true, locked: false, order: 0, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "recipient", enabled: true, locked: false, order: 1, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "invoice_history", enabled: true, locked: false, order: 2, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "payment_history", enabled: true, locked: false, order: 3, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "per_matter_breakdown", enabled: false, locked: false, order: 4, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "fees_table", enabled: false, locked: false, order: 5, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "disbursements_table", enabled: false, locked: false, order: 6, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "totals_summary", enabled: true, locked: true, order: 7, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "notes", enabled: false, locked: false, order: 8, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "footer", enabled: false, locked: false, order: 9, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
  { id: generateBlockId(), blockType: "signature", enabled: false, locked: false, order: 10, settings: { variant: "compact", headingOverride: null, columnsVisible: [] } },
];

interface Props {
  documentType: "invoice" | "account_summary";
}

const BuilderInner: React.FC<Props> = ({ documentType }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, dispatch } = useBuilder();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        if (documentType === "invoice") {
          const res = await invoicesApi.getBuilderData(id);
          const d = res.data;
          const blocks = d.invoice.blockConfig || INVOICE_DEFAULTS;
          dispatch({
            type: "INIT",
            payload: {
              documentType: "invoice",
              documentId: id,
              blocks,
              lineItems: d.invoice.lineItems || [],
              invoiceNumber: d.invoice.invoiceNumber || "",
              dueAt: d.invoice.dueAt || null,
              client: d.client,
              matter: d.matter,
              firm: d.firm,
              feeItems: d.feeItems || [],
              unbilledTimeEntries: d.unbilledTimeEntries || [],
              existingPayments: d.existingPayments || [],
              totals: {
                subtotalCents: d.invoice.subtotalCents || 0,
                discountCents: d.invoice.discountCents || 0,
                taxCents: d.invoice.taxCents || 0,
                totalCents: d.invoice.totalCents || 0,
                paidCents: d.invoice.paidCents || 0,
                balanceDueCents: d.invoice.balanceDueCents || 0,
              },
              notesToClient: d.invoice.notesToClient || "",
              internalNotes: d.invoice.internalNotes || "",
            },
          });
        } else {
          const res = await accountSummariesApi.getBuilderData(id);
          const d = res.data;
          const blocks = d.summary.blockConfig || SUMMARY_DEFAULTS;
          dispatch({
            type: "INIT",
            payload: {
              documentType: "account_summary",
              documentId: id,
              blocks,
              invoices: d.invoices || [],
              payments: d.payments || [],
              matterBreakdowns: d.summary.matterBreakdowns || [],
              client: d.client,
              matter: null,
              firm: d.firm,
              totals: {
                subtotalCents: d.summary.totalInvoicedCents || 0,
                discountCents: 0,
                taxCents: 0,
                totalCents: d.summary.totalInvoicedCents || 0,
                paidCents: d.summary.totalPaidCents || 0,
                balanceDueCents: d.summary.totalOutstandingCents || 0,
              },
              notesToClient: d.summary.notesToClient || "",
              internalNotes: d.summary.internalNotes || "",
            },
          });
        }
      } catch (err) {
        console.error("Failed to load builder data:", err);
        setError("Failed to load document data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, documentType, dispatch]);

  const handleSave = async (): Promise<boolean> => {
    dispatch({ type: "SET_SAVING", isSaving: true });
    try {
      if (documentType === "invoice") {
        await invoicesApi.update(state.documentId, {
          lineItems: state.lineItems,
          blockConfig: state.blocks,
          notesToClient: state.notesToClient || null,
          internalNotes: state.internalNotes || null,
        });
      } else {
        await accountSummariesApi.update(state.documentId, {
          blockConfig: state.blocks,
          notesToClient: state.notesToClient || null,
          internalNotes: state.internalNotes || null,
        });
      }
      dispatch({ type: "MARK_CLEAN" });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      return true;
    } catch (err: any) {
      console.error("Failed to save:", err);
      alert(err.response?.data?.error || "Failed to save document. Please check all fields.");
      return false;
    } finally {
      dispatch({ type: "SET_SAVING", isSaving: false });
    }
  };

  const handleIssue = async () => {
    const saved = await handleSave();
    if (!saved) return;
    try {
      if (documentType === "invoice") {
        await invoicesApi.changeStatus(state.documentId, "issued");
      } else {
        await accountSummariesApi.changeStatus(state.documentId, "issued");
      }
      navigate("/billing");
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to issue document");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-lg text-red-600">{error}</p>
        <button
          onClick={() => navigate("/billing")}
          className="px-4 py-2 rounded-xl border border-slate-200 text-sm hover:bg-slate-50"
        >
          Back to Billing
        </button>
      </div>
    );
  }

  const docLabel = documentType === "invoice" ? "Invoice" : "Statement";

  return (
    <div className="h-screen flex flex-col bg-slate-100 print:bg-white">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/billing")}
            className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800">
              {docLabel} Builder
            </h1>
            <div className="flex items-center gap-1.5">
              {state.isDirty && (
                <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
              )}
              {saveSuccess && (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              )}
              <p className={`text-xs transition-colors ${
                saveSuccess
                  ? "text-emerald-600 font-medium"
                  : state.isDirty
                    ? "text-amber-600"
                    : "text-slate-400"
              }`}>
                {saveSuccess
                  ? "Saved successfully"
                  : state.isDirty
                    ? "Unsaved changes"
                    : "All changes saved"
                }
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-slate-200 ml-2" />
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
          >
            <Printer className="h-4 w-4" />
            Print / PDF
          </button>
          <button
            onClick={handleSave}
            disabled={state.isSaving || !state.isDirty}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all shadow-sm disabled:opacity-40 disabled:shadow-none ${
              saveSuccess
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
            }`}
          >
            {saveSuccess ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {state.isSaving ? "Saving..." : saveSuccess ? "Saved" : "Save"}
          </button>
          <button
            onClick={documentType === "invoice" ? () => setShowIssueModal(true) : handleIssue}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-bold shadow-sm hover:shadow-md transition-all"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-hover, #146C43)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Send className="h-4 w-4" />
            Issue & Send
          </button>
        </div>
      </div>

      {/* Layout */}
      <div className="flex flex-1 overflow-hidden print:block">
        {/* Block drawer (slim rail) */}
        <BlockDrawer />

        {/* Full-width document area */}
        <div className="flex-1 overflow-y-auto p-8 print:p-0 bg-slate-100 print:bg-white">
          <DocumentPreview />
        </div>
      </div>

      {/* Issue & Send Confirmation Modal (invoices only) */}
      {documentType === "invoice" && (
        <IssueAndSendModal
          open={showIssueModal}
          onClose={() => setShowIssueModal(false)}
          onSave={handleSave}
          onComplete={() => navigate("/billing")}
        />
      )}
    </div>
  );
};

const BillingDocumentBuilder: React.FC<Props> = ({ documentType }) => {
  return (
    <BuilderProvider>
      <BuilderInner documentType={documentType} />
    </BuilderProvider>
  );
};

export default BillingDocumentBuilder;
